import React, { useMemo, useEffect, useState } from 'react';
import {
	Box,
	Typography,
	Grid,
	TextField,
	FormControl,
	InputLabel,
	Select,
	MenuItem,
	Autocomplete,
	CircularProgress,
} from '@mui/material';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { useQuery } from 'react-query';
import { fetchAllShifts } from 'core/API/shifts';
import { useWorkplacesContext } from 'module/workplaces/context/WorkplacesContext';
import moment from 'moment-timezone';
import { getTimeOffReasons, TimeOffReason } from '../../../../../core/API/scheduler';

interface CoverRequestFormProps {
	onSubmit: (values: any) => Promise<void>;
	onCancel: () => void;
	currentWeekStart?: Date;
	currentWeekEnd?: Date;
	formRef?: React.MutableRefObject<any>;
}

const validationSchema = Yup.object({
	shiftId: Yup.string().required('Please select a shift to cover'),
});

const CoverRequestForm: React.FC<CoverRequestFormProps> = ({
	onSubmit,
	onCancel: _onCancel,
	currentWeekStart,
	currentWeekEnd,
	formRef,
}) => {
	const { workplaceId } = useWorkplacesContext();
	const currentUserId = localStorage.getItem('userId');
	const [timeOffReasons, setTimeOffReasons] = useState<TimeOffReason[]>([]);
	const [loadingReasons, setLoadingReasons] = useState(true);

	useEffect(() => {
		const fetchTimeOffReasons = async () => {
			try {
				const reasons = await getTimeOffReasons();
				setTimeOffReasons(reasons.filter((r) => r.active && !r.deleted));
			} catch (error) {
				console.error('Failed to fetch time off reasons:', error);
			} finally {
				setLoadingReasons(false);
			}
		};

		fetchTimeOffReasons();
	}, []);

	// Fetch user's shifts for cover request - only for current user and selected week
	const { data: shiftsData, isLoading: shiftsLoading } = useQuery(
		['user-shifts', workplaceId, currentUserId, currentWeekStart, currentWeekEnd],
		() =>
			fetchAllShifts({
				workplaceId,
				fromDate: currentWeekStart ? moment(currentWeekStart).format('YYYY-MM-DD') : undefined,
				toDate: currentWeekEnd ? moment(currentWeekEnd).add(1, 'day').format('YYYY-MM-DD') : undefined,
			}),
		{
			enabled: !!workplaceId && !!currentWeekStart && !!currentWeekEnd && !!currentUserId,
			staleTime: 0,
			cacheTime: 0,
		}
	);

	// Filter user's shifts for the selected week
	const userShifts = useMemo(() => {
		if (!shiftsData?.member || !currentUserId) return [];
		const weekStart = moment(currentWeekStart).startOf('day');
		const weekEnd = moment(currentWeekEnd).endOf('day');
		const now = moment(); // Current time

		const filtered = shiftsData.member.filter((shift: any) => {
			const employeeId =
				shift.employee?.id?.toString() || shift.employee?.toString().replace('/api/employees/', '');
			const shiftStart = moment(shift.startAt || shift.start);

			// Only include published shifts for current user that:
			// 1. Fall within the selected week
			// 2. Start after the current time (future shifts only)
			return (
				employeeId === currentUserId &&
				shiftStart.isBetween(weekStart, weekEnd, null, '[]') &&
				shift.published === true &&
				shiftStart.isAfter(now)
			);
		});

		// Sort by startAt first, then by jobCode label
		return filtered.sort((a: any, b: any) => {
			const startA = moment(a.startAt || a.start);
			const startB = moment(b.startAt || b.start);

			// Compare by start time first
			const startComparison = startA.diff(startB);
			if (startComparison !== 0) return startComparison;

			// If start times are equal, compare by job code label
			const labelA = a.jobCode?.label || '';
			const labelB = b.jobCode?.label || '';
			return labelA.localeCompare(labelB);
		});
	}, [shiftsData, currentUserId, currentWeekStart, currentWeekEnd]);

	const initialValues = {
		shiftId: '',
		reason: '',
		reasonDescription: '',
		reasonNote: '',
		notes: '',
	};

	return (
		<Formik
			innerRef={formRef}
			initialValues={initialValues}
			validationSchema={validationSchema}
			onSubmit={onSubmit}
		>
			{({ values, setFieldValue, errors, touched }) => (
				<Form>
					<Box sx={{ mt: 3 }}>
						<Typography variant="h6" gutterBottom>
							Request Shift Coverage
						</Typography>
						<Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
							Week: {currentWeekStart && moment(currentWeekStart).format('MMM D')} -{' '}
							{currentWeekEnd && moment(currentWeekEnd).format('MMM D, YYYY')}
						</Typography>

						<Grid container spacing={3}>
							<Grid item xs={12}>
								<FormControl fullWidth error={touched.shiftId && !!errors.shiftId}>
									<InputLabel>Select Your Shift *</InputLabel>
									<Select
										value={values.shiftId}
										onChange={(e) => setFieldValue('shiftId', e.target.value)}
										label="Select Your Shift *"
									>
										{shiftsLoading ? (
											<MenuItem value="" disabled>
												<CircularProgress size={20} color="inherit" /> &nbsp; &nbsp; Loading
												shifts...
											</MenuItem>
										) : userShifts.length === 0 ? (
											<MenuItem value="" disabled>
												No shifts available this week
											</MenuItem>
										) : (
											userShifts.map((shift: any) => (
												<MenuItem key={shift.id} value={shift.id}>
													{moment(`${shift.startAt}`).format('ddd, MMM D - h:mm A')} -{' '}
													{moment(`${shift.endAt}`).format('h:mm A')} |{' '}
													{shift.jobCode?.label || 'Shift'}
												</MenuItem>
											))
										)}
									</Select>
									{touched.shiftId && errors.shiftId && (
										<Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 2 }}>
											{errors.shiftId}
										</Typography>
									)}
								</FormControl>
							</Grid>
							<Grid item xs={12}>
								<Autocomplete
									options={timeOffReasons}
									getOptionLabel={(option) => (typeof option === 'string' ? option : option.name)}
									value={timeOffReasons.find((r) => r['@id'] === values.reason) || null}
									onChange={(_, value) => {
										setFieldValue('reason', value ? value['@id'] : '');
										setFieldValue('reasonDescription', value?.description || '');
										setFieldValue('reasonNote', value?.note || '');
									}}
									isOptionEqualToValue={(option, value) => option['@id'] === value?.['@id']}
									loading={loadingReasons}
									disabled={loadingReasons}
									renderInput={(params) => (
										<TextField
											{...params}
											label="Reason"
											fullWidth
										/>
									)}
								/>
								{values.reason && (
									<>
										{values.reasonDescription && (
											<TextField
												fullWidth
												multiline
												rows={2}
												value={values.reasonDescription}
												InputProps={{
													readOnly: true,
												}}
												sx={{ mt: 2 }}
												variant="outlined"
												label="Reason Description"
											/>
										)}
										{values.reasonNote && (
											<TextField
												fullWidth
												multiline
												rows={2}
												value={values.reasonNote}
												InputProps={{
													readOnly: true,
												}}
												sx={{ mt: 2 }}
												variant="outlined"
												label="Reason Note"
											/>
										)}
									</>
								)}
							</Grid>
						<Grid item xs={12}>
							<TextField
								fullWidth
								multiline
								rows={3}
								label="Notes (Optional)"
								value={values.notes}
								onChange={(e) => setFieldValue('notes', e.target.value)}
								placeholder="Add any additional notes or details"
							/>
						</Grid>
					</Grid>
				</Box>
			</Form>
		)}
	</Formik>	);
};

export default CoverRequestForm;